/**
 * Email UI cache (thread list / folder views) on TotalAccessOfflineDB.
 *
 * Legacy `totalaccess_email_local_cache` is migrated once into domain `emails` stores
 * (`connect_cache_*`) — see `lib/offline/emailUiCacheStore.ts`.
 *
 * Optional session AES (AES-GCM + sessionStorage key) still wraps payloads when enabled
 * for parity with older clients; rows are additionally encrypted at rest via `offlinePayloadCrypto`.
 *
 * Retention preference is stored as opaque `emails` meta (`cache_retention_days`), migrated from legacy `emailOfflineRetentionDays` localStorage when missing.
 */

import type { Folder, ThreadListItem } from '../types';

import emailUiCacheStore, {
  EMAIL_UI_STORE_EMAILS,
  EMAIL_UI_STORE_FOLDERS,
  EMAIL_UI_STORE_THREADS,
  getPortalClientId,
  migrateLegacyEmailUICacheDatabaseIfNeeded,
  readLegacyEncryptionEnvelope,
  type CachedThreadPayload,
  type SyncMetaPayload,
} from '@/lib/offline/emailUiCacheStore';
import offlineDb from '@/lib/offline/offlineDb';

const ENCRYPTION_ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12;

/** Opaque meta segment on TotalAccessOfflineDB for offline retention preference (days). */
const CACHE_RETENTION_META_SEGMENT = 'cache_retention_days';

interface EncryptedData {
  iv: string;
  data: string;
  version: number;
}

export interface SyncMetadata {
  folder: string;
  accountId: number;
  lastSyncAt: number;
  lastUid: number;
  messageCount: number;
  unreadCount: number;
}

export interface CachedThread extends ThreadListItem {
  _cacheId?: number;
  _cachedAt?: number;
  _accountId?: number;
  _folder?: string;
}

function isEncryptedEnvelope(value: unknown): value is EncryptedData {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const v = value as EncryptedData;
  return typeof v.iv === 'string' && typeof v.data === 'string' && typeof v.version === 'number';
}

class EmailCacheService {
  private encryptionKey: CryptoKey | null = null;
  private isInitialized = false;
  private initPromise: Promise<void> | null = null;
  private encryptionEnabled = false;
  /** Offline cache retention (days); hydrated from offline meta or legacy localStorage. */
  private retentionDays = 7;

  async init(): Promise<void> {
    if (this.isInitialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = this._doInit();
    return this.initPromise;
  }

  private async hydrateEncryptionPrefs(clientId: number): Promise<void> {
    const fromUnified = await emailUiCacheStore.getEncryptionSetting(clientId);
    if (fromUnified) {
      this.encryptionEnabled = fromUnified.enabled;
      return;
    }

    const legacyFlag = await readLegacyEncryptionEnvelope();
    if (legacyFlag !== null) {
      this.encryptionEnabled = legacyFlag;
      return;
    }

    this.encryptionEnabled = false;
  }

  private readLegacyRetentionLocalStorage(): number {
    if (typeof localStorage === 'undefined') return 7;
    const stored = localStorage.getItem('emailOfflineRetentionDays');
    if (!stored) return 7;
    const n = parseInt(stored, 10);
    return Number.isFinite(n) ? n : 7;
  }

  private clearLegacyRetentionLocalStorage(): void {
    try {
      localStorage?.removeItem('emailOfflineRetentionDays');
    } catch {
      /* ignore */
    }
  }

  private async hydrateRetentionPrefs(clientId: number): Promise<void> {
    if (clientId > 0) {
      try {
        const meta = await emailUiCacheStore.getOpaqueMeta<{ days?: unknown }>(
          clientId,
          CACHE_RETENTION_META_SEGMENT,
        );
        if (typeof meta?.days === 'number' && Number.isFinite(meta.days)) {
          this.retentionDays = meta.days;
          this.clearLegacyRetentionLocalStorage();
          return;
        }
      } catch {
        /* ignore */
      }
      const legacy = this.readLegacyRetentionLocalStorage();
      this.retentionDays = legacy;
      try {
        await emailUiCacheStore.setOpaqueMeta(clientId, CACHE_RETENTION_META_SEGMENT, {
          days: legacy,
        });
        this.clearLegacyRetentionLocalStorage();
      } catch {
        /* ignore */
      }
      return;
    }

    this.retentionDays = this.readLegacyRetentionLocalStorage();
  }

  private async _doInit(): Promise<void> {
    try {
      if (typeof indexedDB === 'undefined') {
        console.warn('[EmailCache] IndexedDB not available');
        this.retentionDays = this.readLegacyRetentionLocalStorage();
        this.isInitialized = true;

        return;
      }

      const clientId = getPortalClientId();

      await this.hydrateEncryptionPrefs(clientId);
      await this.loadOrCreateEncryptionKey();

      await migrateLegacyEmailUICacheDatabaseIfNeeded(async (raw) => {
        if (isEncryptedEnvelope(raw) && this.encryptionEnabled) {
          const dec = await this.decrypt(raw);
          return dec as Record<string, unknown>;
        }
        return raw;
      });

      await this.hydrateRetentionPrefs(clientId);

      this.isInitialized = true;
      console.log('[EmailCache] Initialized (unified TotalAccessOfflineDB)');

      this.pruneOldData()
        .then(({ prunedThreads, prunedEmails }) => {
          if (prunedThreads > 0 || prunedEmails > 0) {
            console.log(`[EmailCache] Startup prune: removed ${prunedThreads} threads, ${prunedEmails} emails`);
          }
        })
        .catch(() => undefined);
    } catch (error) {
      console.error('[EmailCache] Initialization failed:', error);
      throw error;
    }
  }

  private async loadOrCreateEncryptionKey(): Promise<void> {
    try {
      if (!this.encryptionEnabled) {
        this.encryptionKey = null;
        return;
      }

      const storedKeyData = sessionStorage.getItem('emailCacheKey');
      if (storedKeyData) {
        const keyData = JSON.parse(storedKeyData);
        this.encryptionKey = await crypto.subtle.importKey(
          'jwk',
          keyData,
          { name: ENCRYPTION_ALGORITHM, length: KEY_LENGTH },
          true,
          ['encrypt', 'decrypt'],
        );
        return;
      }

      this.encryptionKey = await crypto.subtle.generateKey(
        { name: ENCRYPTION_ALGORITHM, length: KEY_LENGTH },
        true,
        ['encrypt', 'decrypt'],
      );
      const exportedKey = await crypto.subtle.exportKey('jwk', this.encryptionKey);
      sessionStorage.setItem('emailCacheKey', JSON.stringify(exportedKey));
    } catch (error) {
      console.error('[EmailCache] Encryption key error:', error);
      this.encryptionEnabled = false;
      this.encryptionKey = null;
    }
  }

  private async encrypt(data: unknown): Promise<EncryptedData | unknown> {
    if (!this.encryptionEnabled || !this.encryptionKey) {
      return data;
    }

    try {
      const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
      const plaintext = new TextEncoder().encode(JSON.stringify(data));
      const ciphertext = await crypto.subtle.encrypt(
        { name: ENCRYPTION_ALGORITHM, iv },
        this.encryptionKey,
        plaintext,
      );
      const uint8ToBase64 = (bytes: Uint8Array): string => {
        const CHUNK_SIZE = 0x8000;
        let binary = '';
        for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
          const chunk = bytes.subarray(i, Math.min(i + CHUNK_SIZE, bytes.length));
          binary += String.fromCharCode.apply(null, chunk as unknown as number[]);
        }
        return btoa(binary);
      };
      return {
        iv: uint8ToBase64(iv),
        data: uint8ToBase64(new Uint8Array(ciphertext)),
        version: 1,
      };
    } catch (error) {
      console.error('[EmailCache] Encryption failed:', error);
      return data;
    }
  }

  private async decrypt(encrypted: EncryptedData | unknown): Promise<unknown> {
    if (!isEncryptedEnvelope(encrypted)) {
      return encrypted;
    }
    if (!this.encryptionKey) {
      throw new Error('Encryption key not available for decryption');
    }
    const iv = Uint8Array.from(atob(encrypted.iv), (c) => c.charCodeAt(0));
    const ciphertext = Uint8Array.from(atob(encrypted.data), (c) => c.charCodeAt(0));
    const plaintext = await crypto.subtle.decrypt(
      { name: ENCRYPTION_ALGORITHM, iv },
      this.encryptionKey,
      ciphertext,
    );
    return JSON.parse(new TextDecoder().decode(plaintext));
  }

  async updateSyncMetadata(folder: string, accountId: number, metadata: Partial<SyncMetadata>): Promise<void> {
    await this.init();
    const clientId = getPortalClientId();
    if (!clientId) return;
    await emailUiCacheStore.updateSyncMeta(clientId, folder, accountId, metadata as Partial<SyncMetaPayload>);
  }

  async getSyncMetadata(folder: string, accountId: number): Promise<SyncMetadata | null> {
    await this.init();
    const clientId = getPortalClientId();
    if (!clientId) return null;
    const v = await emailUiCacheStore.getSyncMeta(clientId, folder, accountId);
    return v ? (normalizeSyncPayload(v) as SyncMetadata) : null;
  }

  async storeFolders(folders: Folder[], accountId: number): Promise<void> {
    await this.init();
    const clientId = getPortalClientId();
    if (!clientId) return;
    await emailUiCacheStore.storeFolders(
      clientId,
      folders.map((f) => ({ ...f })) as Record<string, unknown>[],
      accountId,
    );
  }

  async getFolders(accountId: number): Promise<Folder[]> {
    await this.init();
    const clientId = getPortalClientId();
    if (!clientId) return [];
    const rows = await emailUiCacheStore.getFolders(clientId, accountId);

    return rows as unknown as Folder[];
  }

  isReady(): boolean {
    return this.isInitialized && offlineDb !== null;
  }

  isEncryptionEnabled(): boolean {
    return this.encryptionEnabled;
  }

  async storeThreads(threads: ThreadListItem[], folder: string, accountId: number): Promise<void> {
    await this.init();
    const clientId = getPortalClientId();
    if (!clientId) return;

    await emailUiCacheStore.storeThreads(
      clientId,
      threads.map((thread) => ({ ...(thread as unknown as CachedThreadPayload) })),
      folder,
      accountId,
    );
  }

  async getThreads(
    folder: string,
    accountId: number,
    options?: { limit?: number; offset?: number },
  ): Promise<CachedThread[]> {
    await this.init();
    const clientId = getPortalClientId();
    if (!clientId) return [];

    const rows = await emailUiCacheStore.getThreads(clientId, folder, accountId, options);
    const out: CachedThread[] = [];
    for (const row of rows) {
      try {
        const unwrapped =
          this.encryptionEnabled && isEncryptedEnvelope(row)
            ? ((await this.decrypt(row)) as Record<string, unknown>)
            : (row as unknown as Record<string, unknown>);
        out.push(unwrapped as unknown as CachedThread);
      } catch {
        /* skip unreadable row */
      }
    }

    return out;
  }

  async hasCachedThreads(folder: string, accountId: number): Promise<boolean> {
    await this.init();
    const clientId = getPortalClientId();
    if (!clientId) return false;
    return emailUiCacheStore.hasCachedThreads(clientId, folder, accountId);
  }

  async getThreadSyncMetadata(folder: string, accountId: number): Promise<SyncMetadata | null> {
    return this.getSyncMetadata(`threads:${folder}`, accountId);
  }

  async updateThreadSyncMetadata(folder: string, accountId: number, metadata: Partial<SyncMetadata>): Promise<void> {
    return this.updateSyncMetadata(`threads:${folder}`, accountId, metadata);
  }

  async clearThreads(folder?: string, accountId?: number): Promise<void> {
    await this.init();
    const clientId = getPortalClientId();
    if (!clientId) return;
    await emailUiCacheStore.clearThreads(clientId, folder, accountId);
  }

  async moveThreadToFolder(
    threadId: string,
    toFolder: string,
    accountId: number,
    updates?: {
      last_message_folder?: string;
      last_sent_at?: string;
      last_sent_preview?: string;
      last_sent_from?: { name?: string; address: string };
    },
  ): Promise<void> {
    await this.init();
    const clientId = getPortalClientId();
    if (!clientId) return;
    await emailUiCacheStore.moveThreadToFolder(clientId, threadId, toFolder, accountId, updates);
  }

  async deleteThread(threadId: string, accountId?: number): Promise<void> {
    await this.init();
    const clientId = getPortalClientId();
    if (!clientId) return;

    await emailUiCacheStore.deleteThread(clientId, threadId, accountId);
  }

  async getStats(): Promise<{
    totalEmails: number;
    totalFolders: number;
    totalThreads: number;
    encryptionEnabled: boolean;
    dbReady: boolean;
  }> {
    await this.init();
    const clientId = getPortalClientId();

    if (!clientId || !offlineDb) {
      return {
        totalEmails: 0,
        totalFolders: 0,
        totalThreads: 0,
        encryptionEnabled: this.encryptionEnabled,
        dbReady: false,
      };
    }

    const [te, tf, tt] = await Promise.all([
      emailUiCacheStore.countRows(clientId, EMAIL_UI_STORE_EMAILS),
      emailUiCacheStore.countRows(clientId, EMAIL_UI_STORE_FOLDERS),
      emailUiCacheStore.countRows(clientId, EMAIL_UI_STORE_THREADS),
    ]);

    return {
      totalEmails: te,
      totalFolders: tf,
      totalThreads: tt,
      encryptionEnabled: this.encryptionEnabled,
      dbReady: true,
    };
  }

  async getDetailedStats(): Promise<{
    totalEmails: number;
    totalFolders: number;
    totalThreads: number;
    oldestEmail: Date | null;
    newestEmail: Date | null;
    estimatedSizeMB: number;
    encryptionEnabled: boolean;
    dbReady: boolean;
  }> {
    const basicStats = await this.getStats();
    await this.init();
    const clientId = getPortalClientId();
    if (!clientId || !offlineDb) {
      return {
        ...basicStats,
        oldestEmail: null,
        newestEmail: null,
        estimatedSizeMB: 0,
      };
    }

    const threads = await emailUiCacheStore.listAllThreadsForPrune(clientId);
    let oldestEmail: Date | null = null;
    let newestEmail: Date | null = null;

    if (threads.length > 0) {
      const dates = threads
        .map((t) => new Date(String(t.last_message_at || t._cachedAt || '')))
        .filter((d) => !Number.isNaN(d.getTime()))
        .sort((a, b) => a.getTime() - b.getTime());
      if (dates.length > 0) {
        oldestEmail = dates[0];

        newestEmail = dates.at(-1)!;
      }
    }

    const estimatedSizeKB = basicStats.totalEmails * 5 + basicStats.totalThreads * 2;

    return {
      ...basicStats,
      oldestEmail,
      newestEmail,
      estimatedSizeMB: Math.round((estimatedSizeKB / 1024) * 100) / 100,
    };
  }

  getRetentionDays(): number {
    return this.retentionDays;
  }

  async setRetentionDays(days: number): Promise<void> {
    this.retentionDays = days;
    await this.init();
    const clientId = getPortalClientId();
    if (clientId > 0) {
      await emailUiCacheStore
        .setOpaqueMeta(clientId, CACHE_RETENTION_META_SEGMENT, { days })
        .catch(() => undefined);
      this.clearLegacyRetentionLocalStorage();
      return;
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('emailOfflineRetentionDays', String(days));
    }
  }

  async pruneOldData(): Promise<{ prunedThreads: number; prunedEmails: number }> {
    const retentionDays = this.getRetentionDays();
    await this.init();
    const clientId = getPortalClientId();

    if (!clientId) {
      return { prunedThreads: 0, prunedEmails: 0 };
    }

    if (retentionDays === 0) {
      const stats = await this.getStats();
      await this.clearAllCache();
      return { prunedThreads: stats.totalThreads, prunedEmails: stats.totalEmails };
    }

    if (retentionDays < 0) {
      return { prunedThreads: 0, prunedEmails: 0 };
    }

    const cutoffDate = Date.now() - retentionDays * 24 * 60 * 60 * 1000;
    let prunedThreads = 0;
    let prunedEmails = 0;

    const threads = await emailUiCacheStore.listAllThreadsForPrune(clientId);
    for (const thread of threads) {
      const d = new Date(thread._cachedAt ?? thread.last_message_at ?? 0).getTime();

      if (d < cutoffDate) {
        const tid = String(thread.thread_id || '');
        if (tid) {
          await emailUiCacheStore.deleteThread(clientId, tid, thread._accountId as number | undefined);

          prunedThreads++;
        }
      }
    }

    const emails = await emailUiCacheStore.listAllEmailsForPrune(clientId);
    for (const mail of emails) {
      const d = new Date((mail.date as string | undefined) || (mail._cachedAt as number | undefined) || 0).getTime();

      if (d < cutoffDate && typeof mail._cache === 'number') {
        await emailUiCacheStore.deleteEmailByRowId(mail._cache);
        prunedEmails++;
      }
    }

    return { prunedThreads, prunedEmails };
  }

  async clearAllCache(): Promise<void> {
    await this.init();
    const clientId = getPortalClientId();
    if (!clientId) return;
    await emailUiCacheStore.clearEverythingForTenant(clientId);
    console.log('[EmailCache] All cache cleared');
  }

  async setEncryptionEnabled(enabled: boolean): Promise<void> {
    await this.init();
    const clientId = getPortalClientId();
    await this.clearAllCache();
    this.encryptionEnabled = enabled;

    if (clientId) {
      await emailUiCacheStore.setEncryptionSetting(clientId, enabled).catch(() => undefined);
    }
    this.encryptionKey = null;
    if (!enabled && typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('emailCacheKey');
    }
    await this.loadOrCreateEncryptionKey();
  }
}

function normalizeSyncPayload(v: Record<string, unknown>): Partial<SyncMetadata> {
  return {
    folder: String(v.folder ?? ''),
    accountId: Number(v.accountId ?? 0),

    lastSyncAt: Number(v.lastSyncAt ?? 0),

    lastUid: Number(v.lastUid ?? 0),
    messageCount: Number(v.messageCount ?? 0),
    unreadCount: Number(v.unreadCount ?? 0),
  };
}

export const emailCacheService = new EmailCacheService();

export default emailCacheService;
